import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, patch, del } from '@/lib/api'

import type { FunctionRole, DeveloperGroup, MemberType, PaginationMeta, ProjectStatus } from '@company/shared'


// ==================== Types ====================

export type { ProjectStatus }



export interface Project {
  id: string
  code: string
  name: string
  description: string | null
  status: ProjectStatus
  ownerId: string
  teamId: string
  startDate: string | null
  endDate: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface ProjectMember {
  id: string
  projectId: string
  employeeId: string
  memberType: MemberType
  functionRole: FunctionRole | null
  developerGroup: DeveloperGroup | null
  isLeader: boolean
  isPrimary: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  employee?: {
    id: string
    name: string
    email: string
    avatar?: string | null
  }
}

export interface MilestoneProgress {
  id: string
  name: string
  totalIssues: number
  completedIssues: number
  progress: number
  status: string
  dueDate: string | null
}

export interface ProjectStatistics {
  totalIssues: number
  completedIssues: number
  completionRate: number
  issuesByStatus: Record<string, number>
  totalEstimatedHours: number
  totalActualHours: number
  overdueIssues: number
  milestoneProgress: MilestoneProgress[]
}

export interface GroupedMembers {
  owner: ProjectMember | null
  function: ProjectMember[]
  developers: Record<DeveloperGroup, ProjectMember[]>
}

export interface ProjectListParams {
  status?: ProjectStatus
  ownerId?: string
  search?: string
  page?: number
  pageSize?: number
  sortBy?: 'createdAt' | 'name' | 'status'
  sortOrder?: 'asc' | 'desc'
}

export interface CreateProjectInput {
  name: string
  description?: string
  startDate?: string
  endDate?: string
  teamId: string
}

export interface UpdateProjectInput {
  name?: string
  description?: string
  startDate?: string
  endDate?: string
  status?: ProjectStatus
}

export interface AddMemberInput {
  employeeId: string
  memberType: MemberType
  functionRole?: FunctionRole
  developerGroup?: DeveloperGroup
  isPrimary?: boolean
}

// ==================== Query Keys ====================

export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (params: ProjectListParams) => [...projectKeys.lists(), params] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  members: (id: string) => [...projectKeys.detail(id), 'members'] as const,
  stats: (id: string) => [...projectKeys.detail(id), 'stats'] as const,
}

// ==================== Hooks ====================

/**
 * 获取项目列表
 */
export function useProjects(params: ProjectListParams = {}) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: async () => {
      const response = await get<Project[]>('/projects', params as Record<string, unknown>)
      return response
    },
  })
}

/**
 * 获取项目列表（带分页元数据）
 */
export function useProjectsWithMeta(params: ProjectListParams = {}) {
  return useQuery({
    queryKey: projectKeys.list(params),
    queryFn: async () => {
      const response = await get<{ data: Project[]; meta: PaginationMeta }>('/projects', params as Record<string, unknown>)
      return response
    },
  })
}

/**
 * 获取单个项目
 */
export function useProject(id: string) {
  return useQuery({
    queryKey: projectKeys.detail(id),
    queryFn: () => get<Project>(`/projects/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建项目
 */
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateProjectInput) => post<Project>('/projects', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
    },
  })
}

/**
 * 更新项目
 */
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectInput }) =>
      patch<Project>(`/projects/${id}`, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
    },
  })
}

/**
 * 归档项目
 */
export function useArchiveProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => post<null>(`/projects/${id}/archive`),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
    },
  })
}

/**
 * 获取项目成员
 */
export function useProjectMembers(projectId: string) {
  return useQuery({
    queryKey: projectKeys.members(projectId),
    queryFn: () => get<GroupedMembers>(`/projects/${projectId}/members`),
    enabled: !!projectId,
  })
}

/**
 * 添加项目成员
 */
export function useAddMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: AddMemberInput }) =>
      post<ProjectMember>(`/projects/${projectId}/members`, data),
    onSuccess: (_, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) })
    },
  })
}

/**
 * 移除项目成员
 */
export function useRemoveMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, memberId }: { projectId: string; memberId: string }) =>
      del<null>(`/projects/${projectId}/members/${memberId}`),
    onSuccess: (_, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) })
    },
  })
}

/**
 * 设置组长
 */
export function useSetGroupLeader() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, memberId }: { projectId: string; memberId: string }) =>
      post<null>(`/projects/${projectId}/members/${memberId}/leader`),
    onSuccess: (_, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) })
    },
  })
}

/**
 * 取消组长
 */
export function useRemoveGroupLeader() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ projectId, memberId }: { projectId: string; memberId: string }) =>
      del<null>(`/projects/${projectId}/members/${memberId}/leader`),
    onSuccess: (_, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.members(projectId) })
    },
  })
}

/**
 * 获取项目统计
 */
export function useProjectStats(projectId: string) {
  return useQuery({
    queryKey: projectKeys.stats(projectId),
    queryFn: () => get<ProjectStatistics>(`/projects/${projectId}/stats`),
    enabled: !!projectId,
  })
}
